// React Hook for AbacatePay Integration
// Custom hook to simplify AbacatePay usage in React components

import { useState, useCallback } from 'react';
import { abacatePayService, CreatePixPaymentRequest, AbacatePaymentResponse } from '../services/abacatePayService';

interface UseAbacatePayState {
  loading: boolean;
  error: string | null;
  payment: AbacatePaymentResponse | null;
}

export const useAbacatePay = () => {
  const [state, setState] = useState<UseAbacatePayState>({
    loading: false,
    error: null,
    payment: null
  });

  const createPixPayment = useCallback(async (request: CreatePixPaymentRequest) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const payment = await abacatePayService.createPixPayment(request);
      setState(prev => ({ ...prev, payment, loading: false }));
      return payment;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
      throw error;
    }
  }, []);

  const checkPaymentStatus = useCallback(async (billingId: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const status = await abacatePayService.getPaymentStatus(billingId);
      setState(prev => ({ ...prev, loading: false }));
      return status;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
      throw error;
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const clearPayment = useCallback(() => {
    setState(prev => ({ ...prev, payment: null }));
  }, []);

  const reset = useCallback(() => {
    setState({
      loading: false,
      error: null,
      payment: null
    });
  }, []);

  return {
    // State
    loading: state.loading,
    error: state.error,
    payment: state.payment,
    
    // Actions
    createPixPayment,
    checkPaymentStatus,
    clearError,
    clearPayment,
    reset,
    
    // Service info
    isAbacatePayEnabled: abacatePayService.isActive(),
    debugInfo: abacatePayService.getDebugInfo()
  };
};

// Utility function to create PIX payment for raffle participation
export const createRafflePixPayment = async (
  participationId: string,
  amount: number,
  customerData: {
    name: string;
    email: string;
    phone?: string;
    taxId?: string;
  },
  raffleInfo: {
    title: string;
    id: string;
  }
): Promise<AbacatePaymentResponse> => {
  const request: CreatePixPaymentRequest = {
    participationId,
    amount: amount * 100, // Convert to cents
    customerName: customerData.name,
    customerEmail: customerData.email,
    customerPhone: customerData.phone,
    customerTaxId: customerData.taxId,
    description: `Participação na rifa: ${raffleInfo.title}`,
    externalId: `raffle_${raffleInfo.id}_participation_${participationId}`
  };

  return await abacatePayService.createPixPayment(request);
};

export default useAbacatePay;